import { createSlice } from '@reduxjs/toolkit'
import { RootState } from '../../../../root.reducer'
import { TransformedAnswerGroupData } from '../efmTypes'
import { CUSTOM_ANSWER_GROUP } from '../efmConstants'
import { efmTemplateApiSlice } from './efmTemplate.api'
import { efmApiSlice } from './efm.api'
import { EvaluationFormSchema } from '../evaluationFormManager.validation'
import { setDisplayOnLoadValue } from './efmForm.slice'

interface GlobalState {
	snackbar: {
		open: boolean,
		message: string,
		error: boolean,
	},
	cloneName: string,
	syncWeights: number[],
	conditionalFields: {
    sectionList: {
      sectionId: number,
      optionList: number[],
    }[],
    questionList: {
      questionId: number,
      optionList: number[],
    }[],
  },
	templateData: TransformedAnswerGroupData[],
	errors: Record<string, string>,
}

const initialState: GlobalState = {
  snackbar: {
    open: false,
    message: '',
    error: false,
  },
  cloneName: '',
  syncWeights: [],
  conditionalFields: {
    sectionList: [{
      sectionId: 1,
      optionList: [],
    }],
    questionList: [{
      questionId: 1,
      optionList: [],
    }],
  },
  templateData: [CUSTOM_ANSWER_GROUP],
  errors: {},
}

const efmGlobalSlice = createSlice({
  name: 'efmGlobal',
  initialState,
  reducers: {
    setCloneName(state, action) {
      state.cloneName = action.payload
    },
    resetCloneName(state) {
      state.cloneName = initialState.cloneName
    },
    openSnackbar(state, action) {
      const { message, error } = action.payload
      state.snackbar = { open: true, message, error }
    },
    closeSnackbar(state) {
      state.snackbar.open = false
    },
    setSyncWeights(state, action: { payload: { idList: number[] } }) {
      const { idList } = action.payload
      state.syncWeights.push(...idList)
    },
    updateSyncWeights(state, action: { payload: { id: number } }) {
      const { id } = action.payload
      const filteredWeights = state.syncWeights.filter(x => x !== id)
      state.syncWeights = filteredWeights
    },
    removeConditionalFields(state, action: { payload: { optionIds: number[] }}) {
      // a display selection field has been deleted
      // we need to check to see if a previous selection exists for that field and remove it
      const { optionIds } = action.payload
      const updatedQuestionFieldList = state.conditionalFields.questionList.map(item => {
        const updatedQuestionOptionList = item.optionList.filter(x => !optionIds.includes(x))
        return { ...item, optionList: updatedQuestionOptionList }
      })
      const updatedSectionFieldList = state.conditionalFields.sectionList.map(item => {
        const updatedSectionOptionList = item.optionList.filter(x => !optionIds.includes(x))
        return { ...item, optionList: updatedSectionOptionList }
      })
      state.conditionalFields = { sectionList: updatedSectionFieldList, questionList: updatedQuestionFieldList }
    },
  },
  extraReducers: builder => {
    builder.addCase(
      setDisplayOnLoadValue, (state, action) => {
        const { sectionOrdinal, questionOrdinal, optionId, idForGlobal } = action.payload
        const { sectionList, questionList } = state.conditionalFields
        // remove previously logged option id in section or question list if it exists
        for (const section of sectionList) {
          section.optionList = section.optionList.filter(id => id !== optionId)
        }
        for (const question of questionList) {
          question.optionList = question.optionList.filter(id => id !== optionId)
        }
        // ADD the option id to the correct section/question path
        if (sectionOrdinal && idForGlobal) {
          // the path might already exists, otherwise create it
          const isSectionInList = sectionList.some(({ sectionId }) => sectionId === idForGlobal)
          isSectionInList ?
            (sectionList.find(({ sectionId }) => sectionId === idForGlobal)?.optionList.push(optionId))
            :
            (sectionList.push({ sectionId: idForGlobal, optionList: [optionId] }))
        }
        if (questionOrdinal && idForGlobal) {
          // the path might already exists, otherwise create it
          const isQuestionInList = questionList.some(({ questionId }) => questionId === idForGlobal)
          isQuestionInList ?
            (questionList.find(({ questionId }) => questionId === idForGlobal)?.optionList.push(optionId))
            :
            (questionList.push({ questionId: idForGlobal, optionList: [optionId] }))
        }
      },
    ),
    builder.addMatcher(
      efmTemplateApiSlice.endpoints.getAnswerGroupTemplates.matchFulfilled,
      (
        state: GlobalState,
        action: { payload: TransformedAnswerGroupData[] },
      ) => {
        state.templateData = action.payload
      },
    ),
    builder.addMatcher(
      efmApiSlice.endpoints.getEvalForm.matchFulfilled,
      (
        state: GlobalState,
        action: { payload: EvaluationFormSchema },
      ) => {
        // first clear all conditional fields
        state.conditionalFields = {
          sectionList: [{
            sectionId: 1,
            optionList: [],
          }],
          questionList: [{
            questionId: 1,
            optionList: [],
          }] }
        // then loop through and add any existing display to ids
        const { sections } =  action.payload
        if (sections) {
          for (const section of sections) {
            const questions = section.questions

            if (questions) {
              for (const question of questions) {
                const options = question.options

                if (options) {
                  for (const option of options) {
                    // find all sections that are being "displayed to"
                    if (option.displaySection !== null) {
                      const target = sections.find(({ order }) => order === option.displaySection)?.id
                      if (target) {
                        const targetSection = state.conditionalFields.sectionList.find(({ sectionId }) => sectionId === target)
                        // the target section should never be in the array so we should always be pushing a new object--leaving this check until further testing is completed
                        targetSection ? targetSection.optionList.push(option.id) : state.conditionalFields.sectionList.push({ sectionId: target, optionList: [option.id] })
                      }
                    }
                    if (option.displayQuestion !== null) {
                      const target = section?.questions?.find(({ order }) => order === option.displayQuestion)?.id
                      if (target) {
                        const targetQuestion = state.conditionalFields.questionList.find(({ questionId }) => questionId === target)
                        // the target question should never be in the array so we should always be pushing a new object--leaving this check until further testing is completed
                        targetQuestion ? targetQuestion.optionList.push(option.id) : state.conditionalFields.questionList.push({ questionId: target, optionList: [option.id] })
                      }
                    }
                  }
                }
              }
            }
          }
        }
      },
    )
  },
})

export const efmGlobal = (state: RootState): GlobalState => state.efmGlobal
export const {
  closeSnackbar,
  openSnackbar,
  removeConditionalFields,
  resetCloneName,
  setCloneName,
  setSyncWeights,
  updateSyncWeights,
} = efmGlobalSlice.actions
export default efmGlobalSlice.reducer
